import { sortBy } from 'lodash';
import { bindAll, nonenumerable } from 'shared/decorators';
import { IA_JOB_STATUS } from 'lbs-pi-platform-access/es/data-server/image-analysis/jobs/consts';
import { isJobInProgress } from 'lbs-pi-platform-access/es/data-server/image-analysis/jobs/util';
import { injectable } from 'shared/stores/core/decorators';
import { makeObservable } from 'shared/stores/core/make-observable';
import { filterEven, filterNone, filterOdd, pushOnce } from 'shared/util';
import {translate} from 'shared/services/I18nService';

@injectable('IA_JOB_BATCH_COORDINATOR')
@bindAll
export class BatchCoordinator {
  /*
  * "Public" props
  */

  state = makeObservable({ // Since the state of the batch coordinator isn't part of a store we manually make it observable.
    regions: {
      queued: [], // These won't be processed until requested via startBatch().,
      scheduled: [], // These will process when the current job is done.,
      running: [], // These are currently in progress.,
      complete: [] // These are done.
    },
    // The batch state is (well, should be) a computed property that returns the state of the current batch.
    batch: null,
    // The batch state is (well, should be) a computed property that returns the state of the current batch.
    scheduled: null
  });

  /*
  * Read-only helper properties:
  */

  get hasRegionsToAnalyze () {
    return this.state.regions.scheduled.length > 0;
  }

  @nonenumerable di = null;
  @nonenumerable jobParams = {
    imageId: null,
    macroId: null,
    inputAnnotationId: null,
    outputAnnotationId: null
  };
  @nonenumerable currentBatch = null;
  @nonenumerable scheduledBatch = null; // keep track of scheduled batch

  constructor (di, imageId, macroId) {
    this.di = di;
    this.jobParams.imageId = imageId;
    this.jobParams.macroId = macroId;
    this.setCurrentBatch(this.createNewBatch());
    this.scheduledBatch = null;
  }

  /*
  * Methods
  */

  @nonenumerable
  configureLayers () {
    // Pull the layer IDs from the regions in the scheduled and running queues
    const layerIds = [];
    [...this.state.regions.scheduled, ...this.state.regions.running].forEach(r => pushOnce(layerIds, r.layerId));
    if (layerIds.length > 1) {
      throw new Error(`Attempted to run job on regions from multiple layers: ${layerIds.join(', ')}`);
    }

    // Derive the "one true" target output layer from the macro Id
    const outputLayer = this.di.get('IA_ANNOTATIONS').getIaOutputLayer(this.jobParams);
    if (outputLayer && layerIds[0] !== outputLayer.inputAnnotationId) {
      throw new Error(`inputAnnotationId mismatch: region layer id: ${layerIds[0]} : outputlayer inputAnnotationId: ${outputLayer.inputAnnotationId}`);
    }
    // Set the job params
    this.jobParams.inputAnnotationId = layerIds[0];
    this.jobParams.outputAnnotationId = outputLayer ? outputLayer.id : null; // Note that setting to "null" will trigger an error when the job starts...
  }

  isMyLayer (layer) {
    return this.jobParams.inputAnnotationId === layer.id || this.jobParams.outputAnnotationId === layer.id;
  }

  addRegion (region) {
    pushOnce(this.state.regions.queued, compareRegions, region);
  }

  updateRegion (region) {
    this.removeRegion(region);
    this.addRegion(region);
  }

  removeRegion (region) {
    const queues = Object.keys(this.state.regions);
    // Note: This aggressively removes all regions from any queue, even in progress. This accurately reflects what
    // happens in DataServer.
    queues.forEach(q => {
      const queue = this.state.regions[q];
      // Note: Check DisplayIds instead of IDs because uScope creates regions with negative IDs, which change
      // after the region is changed. DisplayId doesn't.
      const ix = queue.findIndex(r => r.displayId === region.displayId);
      if (ix > -1) {
        queue.splice(ix, 1);
      }
    });
  }

  removeAllRegions () {
    Object.values(this.state.regions).forEach(q => { q.splice(0); });
  }

  /**
   * Checks to see if there's any outstanding jobs (that we're not already aware of) and watches them.
   * @param regions
   * @returns {Promise<void>}
   */
  async checkForRunningJobs (regions) {
    // Rules:
    // 1. If we're already watching a job exit (to prevent unintentional recursion when a known job finishes)
    // 2. If we're not watching, watch up to the 2 latest jobs. When a job finishes we reload results which will
    //    call this method again, and the process will repeat till there are no more running jobs.
    // Note:
    //    Since there's no way to introspect which regions will be analyzed in any given run we split the regions
    //    between the two queues
    if (this.currentBatch.isRunning) {
      return;
    }
    const jobProvider = this.di.resources.get('IA_JOBS');
    let response = await jobProvider.list(this.jobParams.imageId, this.jobParams.macroId);

    // Filter out finished or errored jobs and sort them. We put the jobs that are (most likely) to finish first at
    // the top of the list. Higher status codes are further along. Same status with greater progress are further
    // along. Same status with same progress (e.g. "queued" and "0") that were submitted first are further along.
    let jobs;
    if (response.data.returnedCount > 0) {
      jobs = sortBy(response.data.response.filter(isJobInProgress), ['status.code', 'status.progress', 'submittedDate']).reverse();
    }

    if (jobs && jobs.length > 0) {
      const runningJob = jobs[0];
      const scheduledJob = jobs[1];

      if (scheduledJob) {
        this.state.regions.scheduled.push(...regions.filter(filterEven));
        if (!this.scheduledBatch) {
          this.scheduledBatch = this.createNewBatch();
          this.state.scheduled = this.scheduledBatch.state;
        }
        this.scheduledBatch.setJob(scheduledJob);
      }

      this.state.regions.running.push(...regions.filter(scheduledJob ? filterOdd : filterNone));
      this.currentBatch.setJob(runningJob);

      this.watchCurrentBatch();
    } else {
      // If all jobs returned are either finished or error'd, then there's no jobs processing the un-analyzed regions.
      // Add them to the queued regions list.
      pushOnce(this.state.regions.queued, compareRegions, ...regions);
    }
  }

  startBatch () {
    safeMove({
      from: this.state.regions.queued,
      to: this.state.regions.scheduled
    });

    this.processQueue();
  }

  startReanalysisBatch (region, inputLayer) {
    const deletedRegion = Object.assign({}, region, {
      displayId: `${region.displayId} (deleted)`,
      layerId: inputLayer.id,
      isDeleted: true
    });
    pushOnce(this.state.regions.queued, compareRegions, deletedRegion);
    this.startBatch();
  }

  cancelRunningBatch () {
    if (this.currentBatch.isRunning) {
      this.currentBatch.cancel();
    }
    // processQueue() will handle status change
  }

  async cancelScheduledBatch () {
    // When a scheduled batch is recovered on load scheduled batch is undefined, which means you can't cancel it. TODO: Fix...
    if (this.scheduledBatch) {
      this.scheduledBatch.cancel();
    }
  }

  @nonenumerable
  async processQueue () {
    if (!this.hasRegionsToAnalyze) {
      return;
    }
    // If a batch is already running, start a new job to represent Submitted in the eSM queue.
    if (this.currentBatch.isRunning) {
      // Don't submit a new job if there's already one queued; IA Job runner will always pick up new regions
      if (!this.scheduledBatch) {
        this.scheduledBatch = this.createNewBatch();
        this.state.scheduled = this.scheduledBatch.state;
        this.tryStart(this.scheduledBatch);
      }
      return;
    }

    safeMove({
      from: this.state.regions.scheduled,
      to: this.state.regions.running
    });

    if (this.scheduledBatch) {
      this.setCurrentBatch(this.scheduledBatch);
      this.scheduledBatch = null;
    } else {
      this.setCurrentBatch(this.createNewBatch());
      await this.tryStart(this.currentBatch);
    }

    await this.watchCurrentBatch();
  }

  @nonenumerable
  async watchCurrentBatch () {
    const $t = translate.scope('ia.batch-coordinator');
    await this.currentBatch.watchJob(); // This will return when the job is complete

    let jobState = this.currentBatch.state;
    let isJobFailedOrCancelled = false;

    if (wasJobCancelled(jobState)) {
      isJobFailedOrCancelled = true;
    } else if (didJobFail(jobState)) {
      isJobFailedOrCancelled = true;
      let message = jobState.statusDetails;
      if (!message || uselessErrorMessages.includes(message)) {
        message = $t('dataserver-logs-information-on-failure');
      }
      this.di.dispatcher.dispatch('IA_JOB_ERROR', {message});
    } else {
      this.di.dispatcher.dispatch('IA_JOB_COMPLETE', this.jobParams);
    }

    if (isJobFailedOrCancelled) {
      safeMove({
        from: this.state.regions.running,
        to: this.state.regions.queued
      });
    } else {
      safeMove({
        from: this.state.regions.running,
        to: this.state.regions.complete
      });
    }

    this.processQueue();
  }

  @nonenumerable
  async tryStart (batch) {
    try {
      this.configureLayers();
      await batch.start();
    } catch ({asMessage, message}) {
      this.di.dispatcher.dispatch('IA_JOB_ERROR', {message: asMessage || message});
    }
  }

  @nonenumerable
  createNewBatch () {
    return this.di.factory.create('IA_JOB_BATCH', this.jobParams);
  }

  @nonenumerable
  setCurrentBatch (batch) {
    this.currentBatch = batch;
    this.state.batch = this.currentBatch.state;
    this.state.scheduled = null;
  }

  pause () {
    this.currentBatch.pause();
  }

  resume () {
    this.currentBatch.resume();
  }
}

/**
 * I'm calling this "safe" move because it protects against unintentionally triggering Vue's mutation detection.
 * splice() and push() are mutating functions; even if nothing exists in either array, Vue will detect changes and
 * trigger watch evaluation. This can lead to infinite loops in some cases (in one annoying-to-troubleshoot case,
 * render was triggering "CANCEL_JOB" which mutated these arrays, causing another render cycle, kicking off another
 * cancel request...). Not doing dumb things like triggering events on render is good, but I'm keeping this here
 * because there's not much value in triggering changes if nothing changed.
 * @param from
 * @param to
 */
function safeMove ({from, to}) {
  if (from.length > 0) {
    to.push(...from.splice(0));
  }
}

const compareRegions = (a) => (b) => a.displayId === b.displayId;

function didJobFail (jobState) {
  return jobState.jobStatus === IA_JOB_STATUS.ERROR;
}

function wasJobCancelled (jobState) {
  return jobState.jobStatus === IA_JOB_STATUS.CANCELLED;
}

// The following error messages from dataserver convey no meaningful error status.
const uselessErrorMessages = [
  '',
  'Algorithm run completed.'
];



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/jobs/BatchCoordinator.js